import type { BlockField } from '@/blocks/domain/types';
import type { TotalContent, TotalPlacement } from '@/blocks/domain/types/BlockContent/TotalContent';
import { formatCurrencyAmount } from '@/common/domain/kit/money';
import { toMoneyAmount } from '@/common/domain/money/toMoneyAmount';

/** Money snapshot the totals renderers already read from document context. */
export interface TotalAmountSnapshot {
  subtotal: number;
  discount_amount: number;
  surcharge_amount: number;
  tax_amount: number;
  grand_total: number;
}

/**
 * What a host may actually send for the totals bag: numbers, numeric / formatted strings
 * ("$3,680.00"), null, or nothing. Every amount is read through `toMoneyAmount`, so none can reach
 * `Intl.NumberFormat` as NaN.
 */
export type TotalAmountInput = { [K in keyof TotalAmountSnapshot]?: unknown };

export type TotalLineSource = 'heading' | 'subtotal' | 'discount' | 'surcharge' | 'tax' | 'grand';

export interface TotalDisplayRow {
  source: TotalLineSource;
  label: string;
  /** Null for the heading row, which has no money cell, and for a money row whose amount is missing. */
  amount: number | null;
  /** True for a money row (subtotal / grand total) the host sent no usable amount for: printed as an em dash. */
  amountMissing?: boolean;
  signedNegative: boolean;
  emphasize: boolean;
}

export const TOTAL_FIELD_IDS: Record<TotalLineSource, string> = {
  heading: 'total-heading',
  subtotal: 'total-subtotal',
  discount: 'total-discount',
  surcharge: 'total-surcharge',
  tax: 'total-tax',
  grand: 'total-grand'
};

const DEFAULT_SOURCES: TotalLineSource[] = ['heading', 'subtotal', 'discount', 'surcharge', 'tax', 'grand'];

const SOURCE_SET = new Set<string>(DEFAULT_SOURCES);

export function isTotalLineSource(value: string): value is TotalLineSource {
  return SOURCE_SET.has(value);
}

export function resolveTotalPlacement(content: TotalContent): TotalPlacement {
  return content.placement === 'left' ? 'left' : 'right';
}

/** HTML preview alignment. Absent placement matches the historical right-aligned block. */
export function totalPlacementCss(placement: TotalPlacement): {
  marginLeft?: 'auto';
  marginRight?: 'auto';
  textAlign: 'left' | 'right';
} {
  if (placement === 'left') return { marginRight: 'auto', textAlign: 'left' };
  return { marginLeft: 'auto', textAlign: 'right' };
}

/** PDF row alignment for the same placement values the HTML renderer reads. */
export function totalPlacementPdfAlign(placement: TotalPlacement): 'flex-start' | 'flex-end' {
  return placement === 'left' ? 'flex-start' : 'flex-end';
}

function orderedSources(rowOrder: string[] | undefined): TotalLineSource[] {
  if (!rowOrder?.length) return [...DEFAULT_SOURCES];
  const picked: TotalLineSource[] = [];
  for (const source of rowOrder) {
    if (isTotalLineSource(source) && !picked.includes(source)) picked.push(source);
  }
  for (const source of DEFAULT_SOURCES) {
    if (!picked.includes(source)) picked.push(source);
  }
  return picked;
}

function fieldFor(source: TotalLineSource, content: TotalContent, sortOrder: number): BlockField {
  const label = labelFor(source, content);
  const visible = visibleFor(source, content);
  return {
    id: TOTAL_FIELD_IDS[source],
    label,
    name: label,
    value: '',
    width: 'full',
    align: 'right',
    style: source === 'heading' ? 'heading' : 'text',
    visible,
    sortOrder
  };
}

function labelFor(source: TotalLineSource, content: TotalContent): string {
  if (source === 'heading') return content.heading?.trim() || 'Heading';
  if (source === 'subtotal') return content.subtotalLabel?.trim() || 'Subtotal';
  if (source === 'discount') return content.discountLabel?.trim() || 'Discount';
  if (source === 'surcharge') return content.surchargeLabel?.trim() || 'Surcharge';
  if (source === 'tax') return content.taxLabel?.trim() || 'Tax';
  return content.grandTotalLabel?.trim() || 'Total';
}

function visibleFor(source: TotalLineSource, content: TotalContent): boolean {
  if (source === 'heading') return Boolean(content.heading?.trim());
  if (source === 'subtotal') return content.showSubtotal;
  if (source === 'discount' || source === 'surcharge' || source === 'tax') return content.showAdjustments;
  return true;
}

export function totalContentToFields(content: TotalContent): BlockField[] {
  return orderedSources(content.rowOrder).map((source, index) => fieldFor(source, content, index));
}

function fieldName(field: BlockField): string {
  return (field.name ?? field.label).trim();
}

function assignOptionalLabel(
  next: TotalContent,
  key: 'subtotalLabel' | 'discountLabel' | 'surchargeLabel' | 'taxLabel',
  name: string,
  fallback: string
): void {
  if (!name || name === fallback) {
    delete next[key];
    return;
  }
  next[key] = name;
}

export function applyTotalFields(content: TotalContent, fields: BlockField[]): TotalContent {
  const projected = new Map(totalContentToFields(content).map((field) => [field.id, field]));
  const incoming = new Map(fields.map((field) => [field.id, field]));
  const next: TotalContent = { ...content };

  const subtotal = incoming.get(TOTAL_FIELD_IDS.subtotal);
  if (subtotal) {
    next.showSubtotal = subtotal.visible;
    const projectedName = fieldName(projected.get(TOTAL_FIELD_IDS.subtotal) ?? subtotal);
    const name = fieldName(subtotal);
    if (name !== projectedName) assignOptionalLabel(next, 'subtotalLabel', name, 'Subtotal');
  }

  const heading = incoming.get(TOTAL_FIELD_IDS.heading);
  const projectedHeading = projected.get(TOTAL_FIELD_IDS.heading);
  if (heading && projectedHeading) {
    if (!heading.visible) {
      delete next.heading;
    } else if (heading.visible !== projectedHeading.visible || fieldName(heading) !== fieldName(projectedHeading)) {
      next.heading = fieldName(heading) || 'Heading';
    }
  }

  const adjustmentSources: Array<'discount' | 'surcharge' | 'tax'> = ['discount', 'surcharge', 'tax'];
  for (const source of adjustmentSources) {
    const field = incoming.get(TOTAL_FIELD_IDS[source]);
    const before = projected.get(TOTAL_FIELD_IDS[source]);
    if (!field || !before) continue;
    if (field.visible !== before.visible) {
      next.showAdjustments = field.visible;
      break;
    }
  }

  const labelKeys = {
    discount: 'discountLabel',
    surcharge: 'surchargeLabel',
    tax: 'taxLabel'
  } as const;
  const fallbacks = { discount: 'Discount', surcharge: 'Surcharge', tax: 'Tax' } as const;
  for (const source of adjustmentSources) {
    const field = incoming.get(TOTAL_FIELD_IDS[source]);
    const before = projected.get(TOTAL_FIELD_IDS[source]);
    if (!field || !before || fieldName(field) === fieldName(before)) continue;
    assignOptionalLabel(next, labelKeys[source], fieldName(field), fallbacks[source]);
  }

  const grand = incoming.get(TOTAL_FIELD_IDS.grand);
  const projectedGrand = projected.get(TOTAL_FIELD_IDS.grand);
  if (grand && projectedGrand && fieldName(grand) !== fieldName(projectedGrand)) {
    next.grandTotalLabel = fieldName(grand) || 'Total';
  }

  const order = orderedSources(undefined);
  const incomingOrder = [...fields]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((field) => sourceForId(field.id))
    .filter((source): source is TotalLineSource => source !== null);
  const merged = mergeMissing(incomingOrder, order);
  const same = merged.length === DEFAULT_SOURCES.length && merged.every((source, index) => source === DEFAULT_SOURCES[index]);
  if (same) delete next.rowOrder;
  else next.rowOrder = merged;

  return next;
}

function sourceForId(id: string): TotalLineSource | null {
  const match = (Object.keys(TOTAL_FIELD_IDS) as TotalLineSource[]).find((source) => TOTAL_FIELD_IDS[source] === id);
  return match ?? null;
}

function mergeMissing(incoming: TotalLineSource[], fallback: TotalLineSource[]): TotalLineSource[] {
  const result = incoming.filter((source, index) => incoming.indexOf(source) === index);
  for (const source of fallback) {
    if (result.includes(source)) continue;
    const insertAt = result.findIndex((existing) => DEFAULT_SOURCES.indexOf(existing) > DEFAULT_SOURCES.indexOf(source));
    if (insertAt === -1) result.push(source);
    else result.splice(insertAt, 0, source);
  }
  return result;
}

function moneyCell(value: unknown): { amount: number | null; amountMissing: boolean } {
  const amount = toMoneyAmount(value);
  return { amount, amountMissing: amount === null };
}

function adjustmentAmount(source: TotalLineSource, amounts: TotalAmountInput): number | null {
  const rawBySource: Partial<Record<TotalLineSource, unknown>> = {
    discount: amounts.discount_amount,
    surcharge: amounts.surcharge_amount,
    tax: amounts.tax_amount
  };
  const amount = toMoneyAmount(rawBySource[source]);
  return amount ? amount : null;
}

function rowFor(source: TotalLineSource, content: TotalContent, amounts: TotalAmountInput): TotalDisplayRow | null {
  if (source === 'heading') {
    const label = content.heading?.trim();
    return label ? { source, label, amount: null, signedNegative: false, emphasize: false } : null;
  }
  if (source === 'subtotal') {
    if (!content.showSubtotal) return null;
    return {
      source,
      label: content.subtotalLabel?.trim() || 'Subtotal',
      ...moneyCell(amounts.subtotal),
      signedNegative: false,
      emphasize: false
    };
  }
  if (source === 'discount' || source === 'surcharge' || source === 'tax') {
    if (!content.showAdjustments) return null;
    const amount = adjustmentAmount(source, amounts);
    if (amount === null) return null;
    return {
      source,
      label: labelFor(source, content),
      amount,
      signedNegative: source === 'discount',
      emphasize: false
    };
  }
  return {
    source: 'grand',
    label: content.grandTotalLabel?.trim() || 'Total',
    ...moneyCell(amounts.grand_total),
    signedNegative: false,
    emphasize: true
  };
}

/** Rows both the HTML and PDF totals renderers print. Legacy content (no extras) matches the previous row set. */
export function totalDisplayRows(content: TotalContent, amounts: TotalAmountInput | null | undefined): TotalDisplayRow[] {
  const rows: TotalDisplayRow[] = [];
  for (const source of orderedSources(content.rowOrder)) {
    const row = rowFor(source, content, amounts ?? {});
    if (row) rows.push(row);
  }
  return rows;
}

const TOTAL_TOKEN_PATHS: Record<Exclude<TotalLineSource, 'heading'>, string> = {
  subtotal: 'totals.subtotal',
  discount: 'totals.discount_amount',
  surcharge: 'totals.surcharge_amount',
  tax: 'totals.tax_amount',
  grand: 'totals.grand_total'
};

/** Any non-zero amount: a row whose amount is absent or zero is hidden, and token rows are never hidden for that. */
const PLACEHOLDER_AMOUNTS: TotalAmountInput = { subtotal: 1, discount_amount: 1, surcharge_amount: 1, tax_amount: 1, grand_total: 1 };

export interface TotalTokenRow {
  source: TotalLineSource;
  label: string;
  /** The `totals.*` variable the row's money cell stands for; null for the heading row. */
  token: string | null;
  emphasize: boolean;
}

/**
 * The rows the totals block shows with Sample data off: every row the author kept visible, each
 * money cell standing for its `totals.*` variable instead of a made-up "$0.00" (an adjustment with
 * no amount yet is still listed, so the author sees the full block).
 */
export function totalTokenRows(content: TotalContent): TotalTokenRow[] {
  return totalDisplayRows(content, PLACEHOLDER_AMOUNTS).map((row) => ({
    source: row.source,
    label: row.label,
    token: row.source === 'heading' ? null : TOTAL_TOKEN_PATHS[row.source],
    emphasize: row.emphasize
  }));
}

/** The text a totals row prints in its money cell — shared by the HTML and the PDF renderer. */
export function formatTotalRowValue(row: TotalDisplayRow, currency: string, cultureTag: string): string {
  if (row.amountMissing) return '—';
  if (row.amount === null) return '';
  const formatted = formatCurrencyAmount(row.signedNegative ? Math.abs(row.amount) : row.amount, currency, cultureTag);
  return row.signedNegative ? `-${formatted}` : formatted;
}
